import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clientDashboardAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FolderOpen, CheckSquare, DollarSign, AlertCircle, FileCheck,
  Clock, Milestone, DoorOpen, Activity, ChevronRight, Circle,
  CheckCircle2, FileText,
} from 'lucide-react';

const STATUS_STYLES = {
  planning: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  nearing_completion: 'bg-orange-100 text-orange-800',
  completed: 'bg-emerald-100 text-emerald-800',
  on_hold: 'bg-gray-100 text-gray-800',
};

const PHASE_STATUS = {
  not_started: { icon: Circle, color: 'text-gray-400' },
  in_progress: { icon: Clock, color: 'text-blue-500' },
  completed: { icon: CheckCircle2, color: 'text-green-500' },
  on_hold: { icon: Clock, color: 'text-amber-500' },
};

const fmt = (n) => typeof n === 'number' ? `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

export default function ClientDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeProject, setActiveProject] = useState(null);

  useEffect(() => {
    clientDashboardAPI.get()
      .then(res => {
        setData(res.data);
        if (res.data?.projects?.length > 0) setActiveProject(res.data.projects[0].project_id);
      })
      .catch(err => console.error('Failed to fetch client dashboard:', err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout>
        <div className="space-y-4 animate-pulse">
          <div className="h-8 bg-muted w-1/3 rounded" />
          <div className="grid grid-cols-4 gap-4">{[1,2,3,4].map(i => <div key={i} className="h-24 bg-muted rounded" />)}</div>
          <div className="h-64 bg-muted rounded" />
        </div>
      </Layout>
    );
  }

  const stats = data?.stats || {};
  const invSummary = data?.invoice_summary || {};
  const projects = data?.projects || [];
  const selectedProject = projects.find(p => p.project_id === activeProject);
  const pendingApprovals = data?.pending_approvals || [];
  const invoices = data?.invoices || [];
  const recentActivity = data?.recent_activity || [];

  return (
    <Layout>
      <div className="space-y-6" data-testid="client-dashboard">
        {/* Welcome */}
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-light tracking-tight">
            Welcome back, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Here's an overview of your design projects</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Projects</p>
                  <p className="text-2xl font-light mt-1" data-testid="client-projects-count">{stats.total_projects}</p>
                </div>
                <div className="p-3 bg-primary/10"><FolderOpen className="h-5 w-5 text-primary" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Task Progress</p>
                  <p className="text-2xl font-light mt-1" data-testid="client-task-progress">{stats.task_completion_rate}%</p>
                  <p className="text-xs text-muted-foreground">{stats.completed_tasks}/{stats.total_tasks} done</p>
                </div>
                <div className="p-3 bg-green-500/10"><CheckSquare className="h-5 w-5 text-green-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Approvals</p>
                  <p className="text-2xl font-light mt-1 text-amber-600" data-testid="client-pending-approvals">{pendingApprovals.length}</p>
                  <p className="text-xs text-muted-foreground">awaiting your response</p>
                </div>
                <div className="p-3 bg-amber-500/10"><AlertCircle className="h-5 w-5 text-amber-600" /></div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-5 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Outstanding</p>
                  <p className="text-2xl font-light mt-1" data-testid="client-outstanding">{fmt(invSummary.total_pending)}</p>
                  <p className="text-xs text-muted-foreground">of {fmt(invSummary.total_invoiced)} total</p>
                </div>
                <div className="p-3 bg-blue-500/10"><DollarSign className="h-5 w-5 text-blue-600" /></div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Project Selector + Detail */}
        {projects.length === 0 ? (
          <Card><CardContent className="py-16 text-center"><FolderOpen className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" /><h3 className="text-lg font-medium mb-2">No projects yet</h3><p className="text-sm text-muted-foreground">Your design projects will appear here once assigned</p></CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Project List */}
            <div className="space-y-2">
              <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground mb-3">Your Projects</h2>
              {projects.map(p => {
                const isActive = p.project_id === activeProject;
                const taskPct = p.task_total > 0 ? Math.round(p.task_completed / p.task_total * 100) : 0;
                return (
                  <button
                    key={p.project_id}
                    onClick={() => setActiveProject(p.project_id)}
                    className={`w-full text-left p-3 rounded border transition-all ${isActive ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`}
                    data-testid={`client-project-${p.project_id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-medium text-sm truncate">{p.name}</h3>
                      <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${isActive ? 'rotate-90' : ''}`} />
                    </div>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[p.status] || 'bg-gray-100 text-gray-800'}`}>{p.status?.replace('_', ' ')}</span>
                    <Progress value={taskPct} className="h-1 mt-2" />
                    <p className="text-[10px] text-muted-foreground mt-1">{taskPct}% tasks done</p>
                  </button>
                );
              })}
            </div>

            {/* Project Detail Panel */}
            <div className="lg:col-span-3 space-y-6">
              {selectedProject && (
                <>
                  {/* Project Header */}
                  <div className="flex items-start justify-between">
                    <div>
                      <h2 className="text-xl font-medium">{selectedProject.name}</h2>
                      <p className="text-sm text-muted-foreground">{selectedProject.city} {selectedProject.current_phase ? `— ${selectedProject.current_phase}` : ''}</p>
                    </div>
                    <Link to={`/projects/${selectedProject.project_id}`} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="view-full-project">
                      View Full Project <ChevronRight className="h-3 w-3" />
                    </Link>
                  </div>

                  {/* Project Progress Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <Card><CardContent className="p-3 text-center"><p className="text-lg font-light">{selectedProject.task_completed}/{selectedProject.task_total}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Tasks Done</p></CardContent></Card>
                    <Card><CardContent className="p-3 text-center"><p className="text-lg font-light">{selectedProject.phases_completed}/{selectedProject.phase_count}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Phases Done</p></CardContent></Card>
                    <Card><CardContent className="p-3 text-center"><p className="text-lg font-light">{selectedProject.room_count}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Rooms</p></CardContent></Card>
                    <Card><CardContent className="p-3 text-center"><p className="text-lg font-light">{selectedProject.files?.length || 0}</p><p className="text-[10px] text-muted-foreground uppercase tracking-wider">Files</p></CardContent></Card>
                  </div>

                  <Tabs defaultValue="phases">
                    <TabsList>
                      <TabsTrigger value="phases" data-testid="client-phases-tab"><Milestone className="h-4 w-4 mr-2" />Phases</TabsTrigger>
                      <TabsTrigger value="rooms" data-testid="client-rooms-tab"><DoorOpen className="h-4 w-4 mr-2" />Rooms</TabsTrigger>
                      <TabsTrigger value="payments" data-testid="client-payments-tab"><DollarSign className="h-4 w-4 mr-2" />Payments</TabsTrigger>
                      <TabsTrigger value="activity" data-testid="client-activity-tab"><Activity className="h-4 w-4 mr-2" />Activity</TabsTrigger>
                    </TabsList>

                    {/* Phases Timeline */}
                    <TabsContent value="phases" className="mt-4">
                      {(selectedProject.phases || []).length === 0 ? (
                        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No phases defined for this project yet</CardContent></Card>
                      ) : (
                        <div className="space-y-1">
                          {(selectedProject.phases || []).map((phase, idx) => {
                            const config = PHASE_STATUS[phase.status] || PHASE_STATUS.not_started;
                            const Icon = config.icon;
                            return (
                              <div key={phase.phase_id} className="flex items-center gap-3 p-3 rounded hover:bg-muted/30 transition-colors" data-testid={`client-phase-${phase.phase_id}`}>
                                <div className="flex flex-col items-center">
                                  <Icon className={`h-5 w-5 ${config.color}`} />
                                  {idx < (selectedProject.phases || []).length - 1 && <div className="w-px h-6 bg-border mt-1" />}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm font-medium ${phase.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>{phase.name}</p>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <span className="capitalize">{phase.status?.replace('_', ' ')}</span>
                                    {phase.billing_percentage > 0 && <span>— {phase.billing_percentage}% billing</span>}
                                    {phase.start_date && <span>— {fmtDate(phase.start_date)}</span>}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </TabsContent>

                    {/* Rooms */}
                    <TabsContent value="rooms" className="mt-4">
                      {(selectedProject.rooms || []).length === 0 ? (
                        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No rooms defined for this project yet</CardContent></Card>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                          {(selectedProject.rooms || []).map(room => (
                            <Card key={room.room_id} data-testid={`client-room-${room.room_id}`}>
                              <CardContent className="p-3">
                                <div className="flex items-center gap-2 mb-1">
                                  <DoorOpen className="h-4 w-4 text-muted-foreground" />
                                  <h4 className="font-medium text-sm">{room.name}</h4>
                                </div>
                                <p className="text-xs text-muted-foreground capitalize">{room.room_type?.replace('_', ' ')}</p>
                                {room.area_sqft && <p className="text-xs text-muted-foreground">{room.area_sqft} sq ft</p>}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      )}
                    </TabsContent>

                    {/* Payments */}
                    <TabsContent value="payments" className="mt-4 space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Total Invoiced</p><p className="text-lg font-light mt-1">{fmt(invSummary.total_invoiced)}</p></CardContent></Card>
                        <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Paid</p><p className="text-lg font-light mt-1 text-green-600">{fmt(invSummary.total_paid)}</p></CardContent></Card>
                        <Card><CardContent className="p-3 text-center"><p className="text-xs text-muted-foreground">Pending</p><p className="text-lg font-light mt-1 text-amber-600">{fmt(invSummary.total_pending)}</p></CardContent></Card>
                      </div>
                      {invoices.length === 0 ? (
                        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No invoices yet</CardContent></Card>
                      ) : (
                        <Card>
                          <CardContent className="p-0">
                            <div className="divide-y">
                              {invoices.map(inv => (
                                <div key={inv.invoice_id} className="flex items-center justify-between p-3 hover:bg-muted/30 transition-colors" data-testid={`client-invoice-${inv.invoice_id}`}>
                                  <div className="flex items-center gap-3">
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                      <p className="text-sm font-medium">{inv.invoice_number}</p>
                                      <p className="text-xs text-muted-foreground">{fmtDate(inv.created_at)}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <p className="text-sm font-medium">{fmt(inv.total)}</p>
                                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full capitalize ${
                                      inv.status === 'paid' ? 'bg-green-100 text-green-700' :
                                      inv.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                                      inv.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                      'bg-gray-100 text-gray-700'
                                    }`}>{inv.status}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>

                    {/* Activity */}
                    <TabsContent value="activity" className="mt-4">
                      {recentActivity.length === 0 ? (
                        <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No recent activity</CardContent></Card>
                      ) : (
                        <Card>
                          <CardContent className="p-0">
                            <div className="divide-y">
                              {recentActivity.map((a, idx) => (
                                <div key={a.activity_id || idx} className="flex items-start gap-3 p-3">
                                  <Activity className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                                  <div>
                                    <p className="text-sm">{a.description || a.action}</p>
                                    <p className="text-xs text-muted-foreground">{fmtDate(a.created_at)}{a.performed_by_name ? ` — ${a.performed_by_name}` : ''}</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </TabsContent>
                  </Tabs>

                  {/* Pending Approvals */}
                  {pendingApprovals.length > 0 && (
                    <Card className="border-amber-200 bg-amber-50/30">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-600" />
                          Pending Your Approval ({pendingApprovals.length})
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {pendingApprovals.map(apr => (
                            <div key={apr.approval_id} className="flex items-start justify-between p-2 bg-white rounded border" data-testid={`client-approval-${apr.approval_id}`}>
                              <div>
                                <p className="text-sm font-medium">{apr.title}</p>
                                <p className="text-xs text-muted-foreground line-clamp-1">{apr.description}</p>
                              </div>
                              <Link to={`/projects/${apr.project_id}?tab=approvals`} className="text-xs text-primary hover:underline shrink-0 ml-3">
                                Review
                              </Link>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
